import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Input } from "../ui/common/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/common/popover";

export interface CompanyOption {
  key: string;
  text: string;
  value: string;
  other?: any;
}

interface CompanySearchDropdownProps {
  options?: CompanyOption[] | null;
  value?: string | null;
  loading?: boolean;
  placeholder?: string;
  onOpen?: () => void;
  onSearchChange?: (query: string) => void;
  onSelect: (option: CompanyOption) => void;
  onClose?: () => void;
}

/**
 * Tailwind/Radix replacement for the Semantic UI search-select, scoped to the
 * header's company switcher only. The shared `DropDown` component this used
 * to reuse is still relied on by ~10 other screens, so it was left alone -
 * this is a new, header-only component instead of a shared-component rewrite.
 */
export const CompanySearchDropdown = ({
  options,
  value,
  loading = false,
  placeholder = "Select Company",
  onOpen,
  onSearchChange,
  onSelect,
  onClose
}: CompanySearchDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const safeOptions = useMemo(() => options ?? [], [options]);

  const selectedLabel = useMemo(
    () => safeOptions.find(option => option.value === value)?.text,
    [safeOptions, value]
  );

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setQuery("");
    if (nextOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="company-select-dropdown"
          className="tw-flex tw-h-9 tw-w-64 tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-text-sm tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-50"
        >
          <span className="tw-truncate">{selectedLabel || placeholder}</span>
          <ChevronDown className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="tw-w-64 tw-p-2">
        <Input
          autoFocus
          placeholder="Search company..."
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            onSearchChange?.(event.target.value);
          }}
          className="tw-mb-2 tw-h-9"
        />
        <div className="tw-max-h-64 tw-overflow-y-auto">
          {loading && <p className="tw-px-2 tw-py-2 tw-text-sm tw-text-gray-500">Loading...</p>}
          {!loading && safeOptions.length === 0 && (
            <p className="tw-px-2 tw-py-2 tw-text-sm tw-text-gray-500">No companies found.</p>
          )}
          {!loading &&
            safeOptions.map(option => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
                className={cn(
                  "tw-flex tw-w-full tw-items-center tw-rounded-md tw-px-2 tw-py-1.5 tw-text-left tw-text-sm hover:tw-bg-gray-100",
                  option.value === value && "tw-bg-gray-100 tw-font-medium"
                )}
              >
                {option.text}
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
